import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, AlertCircle } from "lucide-react";
import { rolesApi, type CreateRoleDto } from "@/lib/api/roles";
import { PermissionMatrix } from "@/components/permissions/PermissionMatrix";
import { ReAuthModal } from "@/components/auth/ReAuthModal";
import { cn } from "@/lib/utils";

const RoleFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");

  const [formData, setFormData] = useState<CreateRoleDto>({
    name: "",
    description: "",
    permissions: [],
  });

  const [permissionGroups, setPermissionGroups] = useState<any[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin) {
      loadPermissionCatalog();
      if (isEditing && id) {
        loadRole(id);
      }
    }
  }, [isTenantAdmin, isEditing, id]);

  const loadPermissionCatalog = async () => {
    try {
      setLoadingCatalog(true);
      setCatalogError(null);
      const catalog = await rolesApi.getPermissionCatalog();
      if (catalog && catalog.groups && Array.isArray(catalog.groups)) {
        setPermissionGroups(catalog.groups);
      } else {
        setCatalogError("Invalid permission catalog format received");
      }
    } catch (error: any) {
      console.error("Failed to load permission catalog:", error);
      setCatalogError(
        error.response?.data?.message || "Failed to load permission catalog. Please try again."
      );
      toast.error("Unable to load permissions. Please try again.");
    } finally {
      setLoadingCatalog(false);
    }
  };

  const loadRole = async (roleId: string) => {
    try {
      setLoading(true);
      const role = await rolesApi.getById(roleId);
      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || [],
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load role.");
      navigate("/setup/roles");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    setErrors(newErrors);
    setTouched(new Set(["name"]));
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    // Show re-auth modal for sensitive operations
    setShowReAuthModal(true);
  };

  const handleReAuthConfirm = async () => {
    if (!reAuthPassword) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setSaving(true);

      if (isEditing && id) {
        await rolesApi.update(id, formData, reAuthPassword);
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        await rolesApi.create(formData, reAuthPassword);
        toast.success(`"${formData.name}" has been created successfully.`);
      }

      setShowReAuthModal(false);
      setReAuthPassword("");
      navigate("/setup/roles");
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error("Invalid password. Please try again.");
        setReAuthPassword("");
      } else {
        toast.error(error.response?.data?.message || "Unable to save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage roles.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading role..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title={isEditing ? "Edit Role" : "Create Role"}
          description={
            isEditing
              ? "Update role permissions and settings"
              : "Create a new role with specific permissions"
          }
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Roles", href: "/setup/roles" },
            { label: isEditing ? "Edit" : "New" },
          ]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Basic Information</h3>
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Librarian, Cataloger"
                  className={cn(
                    touched.has("name") && errors.name && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="description" className="mb-2">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this role is for..."
                  rows={3}
                />
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-6 bg-white shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Permissions</h3>
            {loadingCatalog ? (
              <LoadingSpinner label="Loading permissions..." />
            ) : catalogError ? (
              <div className="p-4 text-center border border-red-200 rounded-lg bg-red-50">
                <AlertCircle className="w-5 h-5 mx-auto mb-2 text-red-600" />
                <p className="text-sm text-red-700">{catalogError}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={loadPermissionCatalog}
                  className="mt-3"
                >
                  Retry
                </Button>
              </div>
            ) : permissionGroups.length > 0 ? (
              <PermissionMatrix
                groups={permissionGroups}
                selectedPermissions={formData.permissions}
                onPermissionsChange={(permissions) => setFormData({ ...formData, permissions })}
              />
            ) : (
              <div className="p-4 text-center text-gray-500">
                No permissions available. Please contact support.
              </div>
            )}
          </Card>

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate("/setup/roles")}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Role"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      <ReAuthModal
        open={showReAuthModal}
        onOpenChange={setShowReAuthModal}
        onConfirm={handleReAuthConfirm}
        password={reAuthPassword}
        onPasswordChange={setReAuthPassword}
        loading={saving}
        title="Re-authentication Required"
        description="Please enter your password to save role changes."
      />
    </AppLayout>
  );
};

export default RoleFormPage;
